import React, { useMemo, useState } from 'react';
import { Form, Button } from 'react-bootstrap';

import { buildNodeIndex, getNodeChildrenOptions, formatControlOptionLabel, getNodePk } from './fscEditClauseUtils';

const MAX_LEVELS = 4;

function pkToString(v) {
  const s = String(v || '').trim();
  return s || null;
}

/**
 * 第一層用：逐層下拉選到葉節點後，在最末層右側顯示「編輯 / 刪除」
 * - 只能選既有樹節點
 * - 可編輯葉節點判定：is_assessable === true 且不是 legacy 唯讀節點
 * - 顯示格式："{control_id} {control_name}"
 * - 刪除：若 leaf 已有草稿 -> 刪草稿；否則清空此 selector 選取（由外層決定）
 */
export default function FSCEditClauseSelectorBlock({
  tree = [],
  draftsByLeafPk = new Map(),
  onEditLeaf,
  onDeleteLeafOrClearSelection,
}) {
  const index = useMemo(() => buildNodeIndex(tree || []), [tree]);

  const [selectedPks, setSelectedPks] = useState(() => Array.from({ length: MAX_LEVELS }, () => null));

  const rootOptions = useMemo(() => {
    const roots = Array.isArray(tree) ? tree : [];
    return roots
      .map((n) => ({
        pk: getNodePk(n),
        label: formatControlOptionLabel(n),
        node: n,
      }))
      .filter((o) => Boolean(o.pk));
  }, [tree]);

  const getOptionsForLevel = (levelIndex) => {
    if (levelIndex === 0) return rootOptions;

    const parentPk = selectedPks[levelIndex - 1];
    if (!parentPk) return [];

    const parentNode = index.byPk.get(parentPk)?.node;
    return getNodeChildrenOptions(parentNode);
  };

  const getIsLevelVisible = (levelIndex) => {
    if (levelIndex === 0) return true;
    if (!selectedPks[levelIndex - 1]) return false;

    // 上一層沒 children，就不顯示下一層
    const parentPk = selectedPks[levelIndex - 1];
    const parentNode = index.byPk.get(parentPk)?.node;
    const children = Array.isArray(parentNode?.children) ? parentNode.children : [];
    return children.length > 0;
  };

  const handleSelectLevel = (levelIndex, pkValue) => {
    const pk = pkToString(pkValue);
    const next = [...selectedPks];

    next[levelIndex] = pk;
    for (let i = levelIndex + 1; i < MAX_LEVELS; i += 1) next[i] = null;

    setSelectedPks(next);
  };

  const lastSelectedPk = useMemo(() => {
    return selectedPks.filter(Boolean).slice(-1)[0] || null;
  }, [selectedPks]);

  const currentLeaf = useMemo(() => {
    if (!lastSelectedPk) return null;
    return index.byPk.get(lastSelectedPk)?.node || null;
  }, [index, lastSelectedPk]);

  const isEditableLeaf = Boolean(currentLeaf?.is_assessable && !currentLeaf?.read_only);
  const leafPk = isEditableLeaf ? getNodePk(currentLeaf) : null;

  // 「編輯 / 刪除」應該出現在最後一個可見的 level row
  const lastVisibleLevelIndex = useMemo(() => {
    let last = 0;
    for (let i = 0; i < MAX_LEVELS; i += 1) {
      if (!getIsLevelVisible(i)) break;
      last = i;
    }
    return last;
  }, [selectedPks]); // eslint-disable-line react-hooks/exhaustive-deps

  const clearSelection = () => {
    setSelectedPks(Array.from({ length: MAX_LEVELS }, () => null));
  };

  const handleClickEdit = () => {
    if (!leafPk) return;
    onEditLeaf && onEditLeaf(leafPk);
  };

  const handleClickDelete = () => {
    if (!leafPk) return;

    // 若已有草稿 -> 外層刪草稿；否則清空此 selector 選取
    const hasDraft = draftsByLeafPk instanceof Map ? draftsByLeafPk.has(leafPk) : false;
    onDeleteLeafOrClearSelection &&
      onDeleteLeafOrClearSelection({
        leafPk,
        hasDraft,
        clearSelection,
      });
  };

  return (
    <div>
      {Array.from({ length: MAX_LEVELS }).map((_, levelIndex) => {
        if (!getIsLevelVisible(levelIndex)) return null;

        const level = levelIndex + 1;
        const options = getOptionsForLevel(levelIndex);
        const value = selectedPks[levelIndex] || '';

        const showActionsOnThisRow = levelIndex === lastVisibleLevelIndex && isEditableLeaf;

        return (
          <div
            key={`fsc-edit-selector-lvl-${levelIndex}`}
            style={{
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'space-between',
              gap: 12,
              marginBottom: 12,
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <Form.Label style={{ fontWeight: 'bold' }}>{`階層${level}`}</Form.Label>
              <Form.Select value={value} onChange={(e) => handleSelectLevel(levelIndex, e.target.value)}>
                <option value="">請選擇</option>
                {options.map((o, idx) => (
                  <option key={o.pk || `fsc-opt-${levelIndex}-${idx}`} value={o.pk || ''}>
                    {o.label}
                  </option>
                ))}
              </Form.Select>
            </div>

            {showActionsOnThisRow ? (
              <div className="d-flex" style={{ gap: 8, paddingBottom: 2 }}>
                <Button variant="text" size="sm" style={{ fontWeight: 'bold' }} onClick={handleClickEdit}>
                  編輯
                </Button>
                <Button variant="text" size="sm" style={{ fontWeight: 'bold' }} onClick={handleClickDelete}>
                  刪除
                </Button>
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
